import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import type { KitResponse, SelectedProduct, AlternativeProduct, FitPreference } from '@nfa/types';
import { getKitById, fetchKit } from '../lib/api';
import { KitResults, LoadingState, ErrorState } from '../components';
import { trackProductSwap } from '../lib/analytics';
import type { ConversionOptions } from '../components/KitConversions';

/** State passed via navigation from fresh search */
interface NavigationState {
  kitData?: KitResponse;
}

interface KitMeta {
  og_title: string;
  og_description: string;
  og_image: string;
  og_site_name: string;
  twitter_card: string;
  canonical_url: string;
}

/**
 * Update document head with OG meta tags
 */
function updateMetaTags(meta: KitMeta) {
  // Helper to set or create a meta tag
  const setMetaTag = (property: string, content: string) => {
    let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('property', property);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };

  const setTwitterTag = (name: string, content: string) => {
    let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', name);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };

  // Update title
  document.title = meta.og_title;

  // OG tags
  setMetaTag('og:title', meta.og_title);
  setMetaTag('og:description', meta.og_description);
  setMetaTag('og:image', meta.og_image);
  setMetaTag('og:site_name', meta.og_site_name);
  setMetaTag('og:url', meta.canonical_url);
  setMetaTag('og:type', 'website');

  // Twitter tags
  setTwitterTag('twitter:card', meta.twitter_card);
  setTwitterTag('twitter:title', meta.og_title);
  setTwitterTag('twitter:description', meta.og_description);
  setTwitterTag('twitter:image', meta.og_image);

  // Canonical link
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', meta.canonical_url);
}

/**
 * Convert an alternative product to a selected product
 * Copies category info from the original and generates a swap reason
 */
function alternativeToSelected(
  original: SelectedProduct,
  alternative: AlternativeProduct
): SelectedProduct {
  return {
    category_id: original.category_id,
    category_name: original.category_name,
    product_id: alternative.product_id,
    product_name: alternative.product_name,
    tier: alternative.tier,
    style: alternative.style,
    price_gbp: alternative.price_gbp,
    weight_g: alternative.weight_g,
    why: `Swapped from ${original.product_name}`,
    affiliate_links: alternative.affiliate_links,
    // Keep the original alternatives but mark current as selected
    alternatives: original.alternatives?.filter(a => a.product_id !== alternative.product_id)
      .concat({
        product_id: original.product_id,
        product_name: original.product_name,
        tier: original.tier,
        style: original.style,
        price_gbp: original.price_gbp,
        weight_g: original.weight_g,
        diff_price: original.price_gbp - alternative.price_gbp,
        diff_weight: original.weight_g - alternative.weight_g,
        affiliate_links: original.affiliate_links,
      }),
    is_shareable: original.is_shareable,
    shared_among: original.shared_among,
    per_person_weight_g: original.per_person_weight_g,
    per_person_price_gbp: original.per_person_price_gbp,
  };
}

/**
 * Recalculate kit totals from items
 */
function recalculateTotals(items: SelectedProduct[]) {
  const price_gbp = items.reduce((sum, item) => sum + item.price_gbp, 0);
  const weight_g = items.reduce((sum, item) => sum + item.weight_g, 0);
  return {
    price_gbp,
    weight_kg: weight_g / 1000,
    weight_g,
    item_count: items.length,
  };
}

export function SavedKit() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navState = location.state as NavigationState | null;

  // Use data from navigation state if available (fresh search), otherwise fetch
  const [kitData, setKitData] = useState<KitResponse | null>(navState?.kitData || null);
  const [isLoading, setIsLoading] = useState(!navState?.kitData);
  const [error, setError] = useState<string | null>(null);

  // Track swaps as categoryId -> productId
  const [swaps, setSwaps] = useState<Record<string, string>>({});

  // Conversion state
  const [isConverting, setIsConverting] = useState(false);
  const [convertingTo, setConvertingTo] = useState<ConversionOptions>();
  const [currentFit, setCurrentFit] = useState<FitPreference>('unisex');
  const navigate = useNavigate();

  // Fetch kit meta for OG tags
  useEffect(() => {
    async function fetchMeta() {
      if (!id) return;

      try {
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.notfuckingabout.com';
        const response = await fetch(`${API_BASE_URL}/api/kit/${id}/meta`);
        if (response.ok) {
          const meta = await response.json() as KitMeta;
          updateMetaTags(meta);
        }
      } catch {
        // Non-critical - silently ignore meta fetch errors
      }
    }

    fetchMeta();

    // Cleanup: restore default title on unmount
    return () => {
      document.title = 'NFA - Gear Recommendations';
    };
  }, [id]);

  useEffect(() => {
    async function loadKit() {
      // Skip fetch if we already have data from navigation state
      if (navState?.kitData) {
        return;
      }

      if (!id) {
        setError('No kit ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const savedKit = await getKitById(id);
        // Transform SavedKit to KitResponse format
        const kitResponse: KitResponse = {
          inference: savedKit.inference,
          kit: savedKit.kit,
          meta: {
            weather: savedKit.weather,
            generated_at: savedKit.created_at,
          },
        };
        setKitData(kitResponse);
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to load kit');
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadKit();
  }, [id, navState?.kitData]);

  const handleNewSearch = () => {
    // Navigate to home page for new search
    window.location.href = '/';
  };

  // Handle product swap
  const handleSwapProduct = useCallback((categoryId: string, productId: string) => {
    // Track analytics
    if (kitData) {
      const original = kitData.kit.items.find(item => item.category_id === categoryId);
      const alternative = original?.alternatives?.find(a => a.product_id === productId);
      if (original && alternative) {
        trackProductSwap(categoryId, original.tier, alternative.tier);
      }
    }
    setSwaps(prev => ({ ...prev, [categoryId]: productId }));
  }, [kitData]);

  // Handle kit conversion (Budget, Ultralight, Premium, Fit changes)
  const handleConvert = useCallback(async (options: ConversionOptions) => {
    if (!kitData) return;

    // Extract the original query from inference
    const query = kitData.inference.location.specific_route ||
      kitData.inference.location.region ||
      `${kitData.inference.duration_days} days ${kitData.inference.activity}`;

    const fit = options.fit || currentFit;

    setIsConverting(true);
    setConvertingTo(options);

    try {
      const newKit = await fetchKit({
        query,
        modifier: options.modifier,
        fit,
      });

      // Update current fit if changed
      if (options.fit) {
        setCurrentFit(options.fit);
      }

      // Navigate to the new saved kit
      if (newKit.savedSlug) {
        navigate(`/kit/${newKit.savedSlug}`, { state: { kitData: newKit } });
      } else {
        // Fallback: update local state
        setKitData(newKit);
        setSwaps({}); // Reset swaps on conversion
      }
    } catch (err) {
      console.error('Conversion failed:', err);
      // Could show error toast here
    } finally {
      setIsConverting(false);
      setConvertingTo(undefined);
    }
  }, [kitData, currentFit, navigate]);

  // Apply swaps to kit data and recalculate totals
  const displayKitData = useMemo((): KitResponse | null => {
    if (!kitData) return null;
    if (Object.keys(swaps).length === 0) return kitData;

    // Create a map of original items by category for quick lookup
    const originalItemsByCategory = new Map<string, SelectedProduct>();
    for (const item of kitData.kit.items) {
      originalItemsByCategory.set(item.category_id, item);
    }

    // Apply swaps to items
    const swappedItems = kitData.kit.items.map(item => {
      const swappedProductId = swaps[item.category_id];
      if (!swappedProductId) return item;

      // Find the alternative to swap to
      const alternative = item.alternatives?.find(a => a.product_id === swappedProductId);
      if (!alternative) return item;

      return alternativeToSelected(item, alternative);
    });

    // Recalculate totals
    const newTotals = recalculateTotals(swappedItems);

    return {
      ...kitData,
      kit: {
        items: swappedItems,
        totals: newTotals,
      },
    };
  }, [kitData, swaps]);

  return (
    <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
      {isLoading && <LoadingState />}

      {error && !isLoading && (
        <div className="max-w-4xl mx-auto text-center">
          <ErrorState message={error} onRetry={() => window.location.reload()} />
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Create a new kit
          </Link>
        </div>
      )}

      {displayKitData && !isLoading && (
        <KitResults
          data={displayKitData}
          onNewSearch={handleNewSearch}
          onSwapProduct={handleSwapProduct}
          onConvert={handleConvert}
          isConverting={isConverting}
          convertingTo={convertingTo}
          currentFit={currentFit}
        />
      )}
    </main>
  );
}
